import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { reportError } from "../lib/errorReporter";
import { fmtDateTime } from "../lib/format";

export default function NotificationBell() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data || []);
    } catch (err) {
      // Non-fatal: the bell simply keeps the previous data on transient errors.
      reportError("notificationBell.refresh", err);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 20000);
    const onOrg = () => refresh();
    const closer = () => setOpen(false);
    window.addEventListener("quowada:orgChanged", onOrg);
    window.addEventListener("click", closer);
    return () => {
      clearInterval(interval);
      window.removeEventListener("click", closer);
      window.removeEventListener("quowada:orgChanged", onOrg);
    };
  }, [refresh]);

  const unread = useMemo(() => items.filter((n) => !n.is_read).length, [items]);

  const markRead = useCallback(
    async (n) => {
      try {
        await api.post(`/notifications/${n.id}/read`);
      } catch (err) {
        // Non-fatal: navigation still happens, refresh below will reconcile.
        reportError("notificationBell.markRead", err);
      }
      if (n.quotation_id) nav(`/quotations/${n.quotation_id}`);
      setOpen(false);
      refresh();
    },
    [nav, refresh],
  );

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/read-all");
      refresh();
    } catch (err) {
      reportError("notificationBell.markAllRead", err);
    }
  }, [refresh]);

  return (
    <div style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
      <button
        className="qw-bell"
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell"
        aria-label={t("nav.notifications")}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {unread > 0 && (
          <span className="count" data-testid="notification-count">{unread}</span>
        )}
      </button>
      {open && (
        <div className="qw-dropdown" data-testid="notification-panel">
          <div style={{ padding: 12, borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between" }}>
            <strong>{t("nav.notifications")}</strong>
            {unread > 0 && (
              <button
                className="qw-btn sm ghost"
                onClick={markAllRead}
                data-testid="mark-all-read"
              >
                {t("notifications:mark_all_read")}
              </button>
            )}
          </div>
          {items.length === 0 && (
            <div className="qw-empty" style={{ padding: 20 }}>{t("notifications.empty")}</div>
          )}
          {items.slice(0, 8).map((n) => (
            <div
              key={n.id}
              className={
                "item " +
                (!n.is_read ? "unread " : "") +
                (String(n.type || "").startsWith("email.") ? "qw-notif-alert" : "")
              }
              onClick={() => markRead(n)}
              data-testid={`notification-item-${n.id}`}
            >
              <div style={{ fontWeight: 600, fontSize: 13 }}>{n.title}</div>
              <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>{n.body}</div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{fmtDateTime(n.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
